import { useCallback, useEffect, useMemo, useRef } from 'react';
import { StaffProfessionControllerApi } from '../api/generated';
import type {
  CreateStaffProfessionRequest,
  StaffProfessionResponse,
  UpdateStaffProfessionRequest
} from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  removeCompetence,
  selectStaffCompetences,
  selectStaffCompetencesError,
  selectStaffCompetencesLoading,
  setError,
  setLoading,
  setCompetences,
  upsertCompetence
} from '../store/staffCompetences/staffCompetencesSlice';
import { getErrorMessage } from '../utils/errorMessageUtils';

export interface UseStaffCompetencesResult {
  competences: StaffProfessionResponse[];
  isLoading: boolean;
  loadError: string | null;
  refresh: () => Promise<void>;
  createCompetence: (
    payload: CreateStaffProfessionRequest
  ) => Promise<StaffProfessionResponse>;
  updateCompetence: (
    id: string,
    payload: UpdateStaffProfessionRequest
  ) => Promise<StaffProfessionResponse>;
  deleteCompetence: (id: string) => Promise<void>;
}

export function useStaffCompetences(): UseStaffCompetencesResult {
  const dispatch = useAppDispatch();
  const competences = useAppSelector(selectStaffCompetences);
  const isLoading = useAppSelector(selectStaffCompetencesLoading);
  const loadError = useAppSelector(selectStaffCompetencesError);
  const hasFetchedRef = useRef(false);
  const api = useMemo(() => new StaffProfessionControllerApi(createApiConfiguration()), []);

  const refresh = useCallback(async () => {
    dispatch(setLoading(true));
    try {
      const response = await api.listStaffProfessions({});
      dispatch(setCompetences(response.data));
    } catch (e) {
      dispatch(setError(getErrorMessage(e, 'Failed to load staff competences')));
      throw e;
    }
  }, [api, dispatch]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    refresh().catch(() => {
      // error already surfaced via slice state
    });
  }, [refresh]);

  const createCompetence = useCallback(
    async (payload: CreateStaffProfessionRequest) => {
      const response = await api.createStaffProfession({
        createStaffProfessionRequest: payload
      });
      dispatch(upsertCompetence(response.data));
      return response.data;
    },
    [api, dispatch]
  );

  const updateCompetence = useCallback(
    async (id: string, payload: UpdateStaffProfessionRequest) => {
      const response = await api.updateStaffProfession({
        professionId: id,
        updateStaffProfessionRequest: payload
      });
      dispatch(upsertCompetence(response.data));
      return response.data;
    },
    [api, dispatch]
  );

  // Backend has no hard delete — archive is the soft-delete equivalent and the only
  // available operation. The list endpoint defaults to excluding archived entries, so we
  // remove from local cache after archive to keep things in sync.
  const deleteCompetence = useCallback(
    async (id: string) => {
      await api.archiveStaffProfession({ professionId: id });
      dispatch(removeCompetence(id));
    },
    [api, dispatch]
  );

  return {
    competences,
    isLoading,
    loadError,
    refresh,
    createCompetence,
    updateCompetence,
    deleteCompetence
  };
}
